/**
 * Facebook Pixel Utility
 * Gestisce tracking eventi Facebook Pixel con GDPR compliance
 * Supporta Conversions API (CAPI) via GTM Server-Side per deduplicazione
 * Include Advanced Matching e customer information parameters per CAPI
 */

const PIXEL_ID = '757480779919360';

// --- Cache user data per Meta events ---
let _cachedUserData = null;
let _cachedHashedUserData = null;

// --- SHA-256 Hashing (Web Crypto API) ---

const hashSHA256 = async (value) => {
  if (!value) return null;
  const encoder = new TextEncoder();
  const data = encoder.encode(value);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(hashBuffer))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
};

// --- Cookie utilities ---

const getCookie = (name) => {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
};

/**
 * Legge _fbc e _fbp cookies.
 * Se _fbc mancante ma fbclid presente in URL, genera _fbc nel formato Meta.
 */
export const getMetaCookies = () => {
  let fbc = getCookie('_fbc');
  const fbp = getCookie('_fbp');

  // Se _fbc mancante, prova a generarlo da fbclid nell'URL
  if (!fbc && typeof window !== 'undefined') {
    try {
      const params = new URLSearchParams(window.location.search);
      const fbclid = params.get('fbclid');
      if (fbclid) {
        fbc = `fb.1.${Date.now()}.${fbclid}`;
      }
    } catch (e) {
      // URL parsing fallito, ignora
    }
  }

  return { fbc, fbp };
};

// --- Normalizzazione dati utente ---

const normalizeEmail = (email) => {
  if (!email) return null;
  return email.trim().toLowerCase();
};

const normalizeName = (name) => {
  if (!name) return null;
  return name.trim().toLowerCase();
};

const normalizePhone = (phone) => {
  if (!phone) return null;
  // Rimuovi spazi, trattini, parentesi
  let cleaned = phone.replace(/[\s\-()]/g, '');
  // Rimuovi + iniziale per la normalizzazione
  if (cleaned.startsWith('+')) {
    cleaned = cleaned.substring(1);
  }
  // Se non inizia con 39 (Italy), aggiungilo
  if (!cleaned.startsWith('39')) {
    // Rimuovi lo 0 iniziale se presente (formato italiano locale)
    if (cleaned.startsWith('0')) {
      cleaned = cleaned.substring(1);
    }
    cleaned = '39' + cleaned;
  }
  return cleaned;
};

const normalizeCity = (city) => {
  if (!city) return null;
  // Lowercase, no spazi, no punteggiatura
  return city.trim().toLowerCase().replace(/[\s,.'"-]/g, '');
};

const normalizeState = (state) => {
  if (!state) return null;
  // Lowercase, no spazi
  return state.trim().toLowerCase().replace(/\s/g, '');
};

/**
 * Cachea i dati utente normalizzati da un profilo Firestore/Auth.
 * Chiamare dopo login/signup. Non fa query Firestore.
 */
export const cacheMetaUserData = (userProfile) => {
  if (!userProfile) return;

  const email = normalizeEmail(userProfile.email);
  const fullName = userProfile.fullName || userProfile.displayName || '';
  const nameParts = fullName.trim().split(/\s+/);
  const fn = normalizeName(nameParts[0]) || null;
  const ln = nameParts.length > 1 ? normalizeName(nameParts.slice(1).join(' ')) : null;
  const ph = normalizePhone(userProfile.phone) || null;
  const ct = normalizeCity(userProfile.comune) || null;
  const st = normalizeState(userProfile.provincia) || null;
  const externalId = userProfile.uid || null;

  _cachedUserData = {
    em: email,
    fn,
    ln,
    ph,
    ct,
    st,
    country: 'it',
    external_id: externalId,
  };

  // Invalida la cache hashata per forzare il ricalcolo
  _cachedHashedUserData = null;

  if (process.env.NODE_ENV === 'development') {
    console.log('[Meta] User data cached for Advanced Matching:', {
      em: email ? '***' : null,
      fn, ln,
      ph: ph ? '***' : null,
      ct, st,
      external_id: externalId ? externalId.substring(0, 8) + '...' : null
    });
  }
};

/**
 * Ritorna dati utente plaintext per fbq('init') Advanced Matching.
 * Il Pixel SDK li hash-a automaticamente.
 */
const getPixelUserData = () => {
  if (!_cachedUserData) return null;
  // Filtra campi null per non inviare valori vuoti
  const data = {};
  if (_cachedUserData.em) data.em = _cachedUserData.em;
  if (_cachedUserData.fn) data.fn = _cachedUserData.fn;
  if (_cachedUserData.ln) data.ln = _cachedUserData.ln;
  if (_cachedUserData.ph) data.ph = _cachedUserData.ph;
  if (_cachedUserData.ct) data.ct = _cachedUserData.ct;
  if (_cachedUserData.st) data.st = _cachedUserData.st;
  if (_cachedUserData.country) data.country = _cachedUserData.country;
  if (_cachedUserData.external_id) data.external_id = _cachedUserData.external_id;
  return Object.keys(data).length > 0 ? data : null;
};

/**
 * Ritorna dati utente SHA-256 hashati per CAPI via GTM dataLayer.
 * Non hash-a: client_user_agent, fbc, fbp (come da documentazione Meta).
 */
const getHashedUserDataForCAPI = async () => {
  if (_cachedHashedUserData) return _cachedHashedUserData;
  if (!_cachedUserData) return null;

  const [em, fn, ln, ph, ct, st, country, external_id] = await Promise.all([
    hashSHA256(_cachedUserData.em),
    hashSHA256(_cachedUserData.fn),
    hashSHA256(_cachedUserData.ln),
    hashSHA256(_cachedUserData.ph),
    hashSHA256(_cachedUserData.ct),
    hashSHA256(_cachedUserData.st),
    hashSHA256(_cachedUserData.country),
    hashSHA256(_cachedUserData.external_id),
  ]);

  const { fbc, fbp } = getMetaCookies();

  const hashed = {};
  if (em) hashed.em = em;
  if (fn) hashed.fn = fn;
  if (ln) hashed.ln = ln;
  if (ph) hashed.ph = ph;
  if (ct) hashed.ct = ct;
  if (st) hashed.st = st;
  if (country) hashed.country = country;
  if (external_id) hashed.external_id = external_id;
  // Non hashare questi parametri (da documentazione Meta)
  hashed.client_user_agent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  if (fbc) hashed.fbc = fbc;
  if (fbp) hashed.fbp = fbp;

  _cachedHashedUserData = hashed;
  return hashed;
};

/**
 * Fallback: prova a caricare user data dalla cache localStorage se non in memoria.
 * Usa la stessa cache di getUser() in firestoreQueries.js.
 */
const tryLoadFromLocalStorageCache = () => {
  if (_cachedUserData) return;
  try {
    const userId = JSON.parse(localStorage.getItem('userId') || 'null');
    if (!userId) return;
    const cached = localStorage.getItem(`user_data_cache_${userId}`);
    if (!cached) return;
    const { data } = JSON.parse(cached);
    if (data?.email) {
      cacheMetaUserData({ ...data, uid: userId });
    }
  } catch (e) {
    // Cache parsing fallito, ignora
  }
};

// --- Public API ---

// Genera un event_id univoco per deduplicazione CAPI
export const generateEventId = () => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 15);
  return `${timestamp}_${random}`;
};

// Verifica se pixel e' caricato
export const isPixelLoaded = () => {
  return typeof window !== 'undefined' && typeof window.fbq === 'function';
};

// Verifica consenso marketing Iubenda
export const hasMarketingConsent = () => {
  if (typeof window === 'undefined' || !window._iub) return false;
  const cs = window._iub?.cs;
  // Finalita' 5 = Marketing in Iubenda. La 4 e' la Misurazione (Google
  // Analytics): leggere la 4 accendeva Meta a chi accettava solo le
  // statistiche (STU-480).
  return cs?.consent?.purposes?.['5'] === true;
};

// Carica pixel dinamicamente (dopo consenso)
export const loadFacebookPixel = () => {
  if (isPixelLoaded()) {
    console.log('Facebook Pixel already loaded');
    return;
  }

  /* eslint-disable no-unused-expressions */
  !function(f,b,e,v,n,t,s) {
    if(f.fbq) return;
    n = f.fbq = function() {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if(!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s);
  }(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable no-unused-expressions */

  // Inizializza con Advanced Matching se dati disponibili
  const pixelUserData = getPixelUserData();
  if (pixelUserData) {
    window.fbq('init', PIXEL_ID, pixelUserData);
  } else {
    window.fbq('init', PIXEL_ID);
  }
  window.fbq('track', 'PageView');

  console.log('Facebook Pixel loaded and initialized');
};

/**
 * Inizializza/aggiorna il pixel con dati utente per Advanced Matching.
 * Chiamare dopo login/signup con il profilo utente da Firestore/Auth.
 */
export const initPixelWithUserData = (userProfile) => {
  if (!userProfile) return;

  // Cachea i dati normalizzati
  cacheMetaUserData(userProfile);

  // Re-inizializza il pixel con Advanced Matching (sicuro chiamare piu' volte)
  if (isPixelLoaded()) {
    const pixelUserData = getPixelUserData();
    if (pixelUserData) {
      window.fbq('init', PIXEL_ID, pixelUserData);
      if (process.env.NODE_ENV === 'development') {
        console.log('[Meta] Pixel re-initialized with Advanced Matching data');
      }
    }
  }
};

/**
 * Pulisce i dati utente cachati. Chiamare al logout.
 */
export const clearMetaUserData = () => {
  _cachedUserData = null;
  _cachedHashedUserData = null;
};

// Attende che il pixel sia caricato
export const waitForPixelLoad = (maxAttempts = 10, interval = 500) => {
  return new Promise((resolve, reject) => {
    let attempts = 0;

    const checkPixel = () => {
      attempts++;

      if (isPixelLoaded()) {
        console.log('Facebook Pixel ready for tracking');
        resolve(true);
        return;
      }

      if (attempts >= maxAttempts) {
        console.warn(`Facebook Pixel not loaded after ${maxAttempts} attempts`);
        reject(new Error('Pixel load timeout'));
        return;
      }

      setTimeout(checkPixel, interval);
    };

    checkPixel();
  });
};

// Traccia evento standard con supporto CAPI deduplication e user_data
export const trackEvent = (eventName, params = {}) => {
  if (!isPixelLoaded()) {
    console.warn(`Facebook Pixel not loaded. Event "${eventName}" skipped.`);
    return false;
  }

  try {
    // event_id per deduplicazione con Conversions API: se il chiamante ne passa
    // uno (es. purchase_${session_id}, identico a quello del webhook CAPI) va
    // usato come eventID reale — prima veniva sempre rigenerato e quello del
    // chiamante finiva nei params come campo custom, rompendo la dedup.
    const { event_id, eventID, ...cleanParams } = params;
    const eventId = event_id || eventID || generateEventId();

    // Aggiungi eventID ai parametri per il pixel browser-side
    const paramsWithEventId = {
      ...cleanParams,
      eventID: eventId  // Facebook usa eventID (camelCase)
    };

    window.fbq('track', eventName, paramsWithEventId);

    // Fallback: prova a caricare user data da localStorage se non in memoria
    tryLoadFromLocalStorageCache();

    // Invia anche a GTM dataLayer per il server-side tracking (CAPI), ma SOLO
    // col consenso al marketing (STU-480). L'evento porta email, telefono e
    // nome in hash: senza questo controllo arrivava a GTM/Stape anche a chi
    // aveva rifiutato, e il contenitore poteva inoltrarlo a Meta scavalcando
    // il banner. La chiamata fbq qui sopra resta nella coda dello stub
    // (index.html) e parte solo se il consenso arriva.
    if (typeof window !== 'undefined' && window.dataLayer && hasMarketingConsent()) {
      // Prepara user_data hashati per CAPI (async, fire-and-forget)
      getHashedUserDataForCAPI().then(userData => {
        const dataLayerEvent = {
          event: 'fb_event',
          fb_event_name: eventName,
          fb_event_id: eventId,
          fb_event_params: cleanParams,
        };
        if (userData && Object.keys(userData).length > 0) {
          dataLayerEvent.user_data = userData;
        }
        window.dataLayer.push(dataLayerEvent);
      }).catch(() => {
        // Fallback senza user_data se hashing fallisce
        window.dataLayer.push({
          event: 'fb_event',
          fb_event_name: eventName,
          fb_event_id: eventId,
          fb_event_params: cleanParams,
        });
      });
    }

    // Log solo in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`FB Pixel: ${eventName}`, paramsWithEventId);
    }
    return true;
  } catch (error) {
    console.error(`Error tracking FB Pixel event "${eventName}":`, error);
    return false;
  }
};

// Eventi standard Facebook
export const FacebookPixelEvents = {
  PURCHASE: 'Purchase',
  SUBSCRIBE: 'Subscribe',
  START_TRIAL: 'StartTrial',
  COMPLETE_REGISTRATION: 'CompleteRegistration',
  LEAD: 'Lead',
  VIEW_CONTENT: 'ViewContent',
  INITIATE_CHECKOUT: 'InitiateCheckout',
  ADD_PAYMENT_INFO: 'AddPaymentInfo',
  ADD_TO_CART: 'AddToCart',
  SEARCH: 'Search',
};
